/**
 * PE-2220 — payload helpers for the product create/edit transforms.
 *
 * Both transforms used to paper over an unresolved metadata id with the literal
 * string `"string"` (the swagger example value). Every one of those ids is
 * `@IsUUID()` on rack-api's `CreateProductDto`, and `PartialType` only skips
 * validation for `undefined` — a present-but-invalid value still fails. So the
 * sentinel turned "the merchant skipped an optional field" into an opaque
 * `422 sizeGuideId must be a UUID`, which is exactly the class of bug PE-1283
 * set out to remove ("optional metadata can be skipped without validation
 * errors").
 *
 * The rule now: an optional id is OMITTED when unresolved, and a structurally
 * required id (one nested inside the `sizes[].materials[].colours[]` tree, which
 * has no representation for "absent") raises a named error the caller can show.
 */

/** Thrown when an id the payload structurally requires cannot be resolved. */
export class UnresolvedMetadataError extends Error {
  /** e.g. `material` / `colour` — what could not be resolved. */
  readonly kind: string
  /** the human-facing label the merchant picked, e.g. `Silk` or `#B08D57`. */
  readonly label: string

  // Fields are declared and assigned explicitly rather than via constructor
  // parameter properties: this project builds with `erasableSyntaxOnly`, which
  // rejects that TS-only syntax (TS1294).
  constructor(kind: string, label: string) {
    super(
      `Could not match ${kind} "${label}" to this merchant's catalogue. ` +
        `Re-pick it in the variant editor, or add it under Product Metadata first.`,
    )
    this.kind = kind
    this.label = label
    this.name = 'UnresolvedMetadataError'
  }
}

/**
 * An id the API treats as optional. Returns `undefined` for blank/whitespace so
 * the caller can omit the key entirely rather than send an invalid value.
 */
export const optionalId = (value?: string | null): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

/** Spread helper: `...optionalIdField('sizeGuideId', formData.styleGuide)`. */
export const optionalIdField = (
  key: string,
  value?: string | null,
): Record<string, string> => {
  const id = optionalId(value)
  return id ? { [key]: id } : {}
}

/**
 * An id the payload structurally requires. Throws `UnresolvedMetadataError`
 * instead of emitting a sentinel the server will reject with a worse message.
 */
export const requiredId = (
  value: string | undefined | null,
  kind: string,
  label: string,
): string => {
  const id = optionalId(value)
  if (!id) throw new UnresolvedMetadataError(kind, label)
  return id
}
